import { localizedStrings } from '@core/strings'
import React from 'react'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
} from '@common/material'
import { Box, Flex, Text } from '@common/fundamentals'
import ClearIcon from '@mui/icons-material/Clear'
import { Image } from '@fundamentals'
import { TableSecondaryText } from '@common/Table'
import moment from 'moment'
import {
  ArticleOutlined,
  AssignmentOutlined,
  ChevronRight,
  FactCheckOutlined,
} from '@mui/icons-material'
import { PermitDetailsModal } from '@modules/permitDetails/PermitDetailsModal'
import {
  DuplicatedInType,
  ImageDuplicate,
  ImageDuplicatedIn,
} from '@core/entities/ImageWarning'
import { ModalDialogContainer } from '@common/modal'
import ChecklistDetailsModal from '@modules/permitDetails/modals/ChecklistDetailsModal'
import { TaskDetailsModal } from '@modules/tasks/taskDetails/modals/TaskDetailsModal'

interface ComponentType {
  organisationId: number
  imageDuplicates: Array<ImageDuplicate>
  timelineDownloadUrls?: Array<string>
}

export const DuplicatedImagePermitModal: React.FC<ComponentType> = ({
  organisationId,
  imageDuplicates,
  timelineDownloadUrls,
}) => {
  const { hideModal } = useModal()

  return (
    <ModalDialogContainer maxWidth='lg'>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.duplicatedImageDetected}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}
      >
        {imageDuplicates.map((imageDuplicate, index) => {
          const imageUrl = timelineDownloadUrls
            ? timelineDownloadUrls.find((url) =>
                url.includes(imageDuplicate.mediaKey),
              )
            : imageDuplicate?.downloadUrl
          return (
            <Box key={index}>
              <Text fontWeight='bold'>
                {localizedStrings.duplicatedImageDetectedModalDescription}
              </Text>
              {imageDuplicate?.downloadUrl && (
                <>
                  <Image my={2} src={imageUrl} width={170} />
                  <Divider sx={{ pb: 2 }} />
                </>
              )}
              {imageDuplicate?.duplicatedIn?.map((duplicatedIn) => {
                return (
                  <DuplicateItem
                    key={duplicatedIn.id}
                    organisationId={organisationId}
                    duplicatedIn={duplicatedIn}
                  />
                )
              })}
            </Box>
          )
        })}
      </DialogContent>
    </ModalDialogContainer>
  )
}

const DuplicateItem = ({
  organisationId,
  duplicatedIn,
}: {
  organisationId: number
  duplicatedIn: ImageDuplicatedIn
}) => {
  const { showModal } = useModal()
  // TODO: have backend return the duplicate permit information we need to display the list so that we don't have to fetch a lot of permits

  const { id: permitId, uploadedBy, uploadOn, type } = duplicatedIn
  const uploadDate = moment(uploadOn).format('DD MMM yyyy')

  const getModalByType = (type: DuplicatedInType) => {
    switch (type) {
      case 'PERMIT':
        return (
          <PermitDetailsModal
            organisationId={organisationId}
            permitId={permitId}
          />
        )
      case 'CHECKLIST':
        return (
          <ChecklistDetailsModal
            organisationId={organisationId}
            checklistId={permitId}
          />
        )
      case 'TASK':
        return <TaskDetailsModal taskId={permitId} />
      default:
        return null
    }
  }

  const getTypeIcon = (type: DuplicatedInType) => {
    switch (type) {
      case 'PERMIT':
        return <ArticleOutlined />
      case 'CHECKLIST':
        return <FactCheckOutlined />
      case 'TASK':
        return <AssignmentOutlined />
      default:
        return null
    }
  }

  return (
    <MenuItem
      onClick={() => showModal(getModalByType(type))}
      sx={{
        py: 2,
        width: '100%',
        borderBottom: 1,
        borderColor: 'divider',
      }}
      key={permitId}
    >
      <Flex alignItems='center' justifyContent='space-between' width='100%'>
        <Box>
          <Flex alignItems='center' gap={0.5} justifyContent='flex-start'>
            {getTypeIcon(type)}
            <Text>{duplicatedIn.getTitle()}</Text>
          </Flex>
          <TableSecondaryText>
            {localizedStrings.uploadedByAt(uploadedBy, uploadDate)}
          </TableSecondaryText>
        </Box>

        <ChevronRight />
      </Flex>
    </MenuItem>
  )
}
